import type { Ref } from 'vue';
import { nextTick } from 'vue';
import { RoleEnum, userApi } from '@/entities/User';
import { parseCivilDate } from '@/shared/lib';
import type { FunctionalActivityItem } from '../ui/UserFormContent.vue';

interface UseEditUserDataParams {
  name: Ref<string>;
  username: Ref<string>;
  email: Ref<string>;
  registrationNumber: Ref<string>;
  admissionDate: Ref<Date | null>;
  dismissalDate: Ref<Date | null>;
  status: Ref<string>;
  isSuperadmin: Ref<boolean>;
  functionalActivities: Ref<FunctionalActivityItem[]>;
  isLoadingUser: Ref<boolean>;
  isHydratingUserData?: Ref<boolean>;
  loadDepartments: () => Promise<void>;
  loadFunctionalActivities: () => Promise<void>;
  onUserLoaded?: () => void;
}

export function useEditUserData(params: UseEditUserDataParams) {
  let activeUserId: number | null = null;
  let activeRequest: Promise<void> | null = null;

  const loadUserData = async (userId: number) => {
    if (activeUserId === userId && activeRequest) {
      return activeRequest;
    }

    activeUserId = userId;
    params.isLoadingUser.value = true;
    activeRequest = (async () => {
      try {
        const [user] = await Promise.all([
          userApi.getUserById(userId),
          params.loadDepartments(),
          params.loadFunctionalActivities(),
        ]);

        if (params.isHydratingUserData) {
          params.isHydratingUserData.value = true;
        }

        params.name.value = user.name ?? '';
        params.username.value = user.username ?? '';
        params.email.value = user.email ?? '';
        params.registrationNumber.value = user.registrationNumber ?? '';
        params.status.value = user.status ?? 'ACTIVE';
        params.isSuperadmin.value = user.role === RoleEnum.SUPERUSER;
        params.admissionDate.value = parseCivilDate(user.admissionDate);
        params.dismissalDate.value = parseCivilDate(user.dismissalDate);

        const activities = (user.memberships ?? [])
          .flatMap(m => m.functionalActivities ?? [])
          .map(fa => ({ functionalActivityId: fa.functionalActivity.id }));
        params.functionalActivities.value =
          activities.length > 0 ? activities : [{ functionalActivityId: '' }];

        await nextTick();

        params.onUserLoaded?.();
      } finally {
        if (params.isHydratingUserData) {
          params.isHydratingUserData.value = false;
        }
        params.isLoadingUser.value = false;
        activeRequest = null;
        activeUserId = null;
      }
    })();

    return activeRequest;
  };

  return { loadUserData };
}
